import React, { Suspense } from 'react';

import UnreadBadgeSync from './src/components/notifications/UnreadBadgeSync';
import HashSectionHighlighter from './src/components/forms/HashSectionHighlighter';
import PresenterReturnButton from './src/lib/presenter/PresenterReturnButton';
import { NavMemory } from './src/lib/navigation/backNav';
import RedirectKeepingQuery, { RetiredTabRedirect } from './src/lib/navigation/RedirectKeepingQuery';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Auth Context
import AuthProvider from './src/context/AuthContext';
import { ThemeProvider } from './src/context/ThemeContext';
import { UnreadCountsProvider } from './src/context/UnreadCountsContext';
import { FeatureFlagsProvider } from './src/context/FeatureFlagsContext';

// Dev Navigation Hub
const NavigationHub = lazyWithRetry(() => import('./src/components/NavigationHub'));

// Landing Page + Root Route
import RootRoute from './src/pages/RootRoute';
const LandingPage = lazyWithRetry(() => import('./src/components/LandingPage'));
const SignupPage = lazyWithRetry(() => import('./src/components/SignupPage'));
const SSORoleSelection = lazyWithRetry(() => import('./src/pages/SSORoleSelection'));

// EMT Components (public routes - keep eager loaded for faster initial render)
const EMTWelcome = lazyWithRetry(() => import('./src/components/EMTWelcome'));
const EMTRegStep1 = lazyWithRetry(() => import('./src/components/EMTRegStep1'));
const EMTRegStep2 = lazyWithRetry(() => import('./src/components/EMTRegStep2'));
const EMTRegStep3 = lazyWithRetry(() => import('./src/components/EMTRegStep3'));
const EMTRegStep4 = lazyWithRetry(() => import('./src/components/EMTRegStep4'));
const EMTRegQuickQuestions = lazyWithRetry(() => import('./src/components/EMTRegQuickQuestions'));
const EMTRegWorkExperience = lazyWithRetry(() => import('./src/components/EMTRegWorkExperience'));
const InstituteRegistration = lazyWithRetry(() => import('./src/pages/institute/InstituteRegistration'));
const InstituteDashboard = lazyWithRetry(() => import('./src/pages/institute/InstituteDashboard'));
const InstitutePublicPage = lazyWithRetry(() => import('./src/pages/institute/InstitutePublicPage'));
const InstituteRenewals = lazyWithRetry(() => import('./src/pages/institute/InstituteRenewals'));
const InstitutePrograms = lazyWithRetry(() => import('./src/pages/institute/InstitutePrograms'));
const InstituteLocations = lazyWithRetry(() => import('./src/pages/institute/InstituteLocations'));
const InstituteProfile = lazyWithRetry(() => import('./src/pages/institute/InstituteProfile'));
const InstituteSettings = lazyWithRetry(() => import('./src/pages/institute/InstituteSettings'));
const InstituteClasses = lazyWithRetry(() => import('./src/pages/institute/InstituteClasses'));
const InstituteClassRoster = lazyWithRetry(() => import('./src/pages/institute/InstituteClassRoster'));
const InstituteGraduate = lazyWithRetry(() => import('./src/pages/institute/InstituteGraduate'));
const ClassInvitePage = lazyWithRetry(() => import('./src/pages/ClassInvitePage'));
const InstituteReviewPage = lazyWithRetry(() => import('./src/pages/InstituteReviewPage'));
const InstituteContactUnsubscribe = lazyWithRetry(() => import('./src/pages/InstituteContactUnsubscribe'));
const EmployerTeamOnboarding = lazyWithRetry(() => import('./src/pages/employer/EmployerOnboarding'));
const EmployerJobView = lazyWithRetry(() => import('./src/pages/employer/EmployerJobView'));
const EmployerCredentials = lazyWithRetry(() => import('./src/pages/employer/EmployerCredentials'));
const DocumentRequestsPage = lazyWithRetry(() => import('./src/pages/emt/DocumentRequestsPage'));
const PayrollDetailsPage = lazyWithRetry(() => import('./src/pages/emt/PayrollDetailsPage'));
const HireChecklistPage = lazyWithRetry(() => import('./src/pages/emt/HireChecklistPage'));
import EMTNotificationsChat from './src/components/EMTNotificationsChat';

// Lazy loaded components (code-splitting for authenticated routes)
import {
  EMTDashboard,
  EMTPerformance,
  EMTAccountDetails,
  Messages,
  Notifications,
  Jobs,
  JobDetail,
  JobApplication,
  Applications,
  FindTraining,
  EmployerDashboardHome,
  EmployerDashboard,
  EmployerCompanySetup,
  EmployerJobForm,
  EmployerPostJob,
  EmployerPostEmergencyJob,
  EmployerJobsPage,
  EmployerJobApplicants,
  EmployerMessages,
  EmployerSearch,
  EmployerAccount,
  EmployerEditCompany,
  EmployerCompanyProfile,
  EmployerCompanyChecklist,
  EmployerMatches,
  OrganizationPublicProfile,
  EmployerEmtProfile,
  EmployerNotifications,
  EmployerWorkforce,
  EmployerJobBenchmarks,
  EmployerScheduling,
  CandidateBookingPage,
  ScheduleInvitePage,
  InterviewRequestPage,
  EmployerSsoBackupPassword,
  EmployerApplicationDetail,
  PastLogs,
  MyCredentialing,
  OrientationPage,
  EmployerHiringInsights,
  PublicProfile,
  EMTCompanyMap,
  CareerWallet,
} from './src/AppRoutes';

// Employer Components (public routes)
import EmployerProfileRedirect from './src/components/EmployerProfileRedirect';
const EmployerLogin = lazyWithRetry(() => import('./src/components/EmployerLogin'));
const EmployerRegistration = lazyWithRetry(() => import('./src/components/EmployerRegistration'));
const EmployerChoosePlan = lazyWithRetry(() => import('./src/components/EmployerChoosePlan'));
const EmployerNavigationHub = lazyWithRetry(() => import('./src/components/EmployerNavigationHub'));
import PlansRouteRedirect from './src/components/PlansRouteRedirect';
import { plansEnabled } from './src/config/featureFlags';

// Auth Protection
import ProtectedRoute from './src/components/ProtectedRoute';
import RoleRoute from './src/components/RoleRoute';
import RequireEmployer from './src/components/RequireEmployer';
import EMTRouteGuard from './src/components/EMTRouteGuard';
import EmployerRouteGuard from './src/components/EmployerRouteGuard';
import EMTLayout from './src/components/EMTLayout';
import PublicJobGate from './src/pages/public/PublicJobGate';
import { lazyWithRetry } from './src/lib/lazyWithRetry';
import EmployerNav from './src/components/employer/EmployerNav';
import { useAuth } from './src/context/AuthContext';

// Dev Tools
const DevHealthCheck = lazyWithRetry(() => import('./src/components/DevHealthCheck'));
const DebugFirebase = lazyWithRetry(() => import('./src/pages/DebugFirebase'));
import DebugNavWatcher from './src/DebugNavWatcher';
import BuildVersion from './src/components/BuildVersion';
const BuildVersionPage = lazyWithRetry(() => import('./src/pages/BuildVersionPage'));
import AppErrorBoundary from './src/components/AppErrorBoundary';
import OfflineBanner from './src/components/OfflineBanner';
import PracticeBanner from './src/components/PracticeBanner';
// Legal Pages
const PrivacyPolicy = lazyWithRetry(() => import('./src/pages/PrivacyPolicy'));
const VerifyManagerPage = lazyWithRetry(() => import('./src/pages/VerifyManagerPage'));
const TeamInviteAccept = lazyWithRetry(() => import('./src/pages/TeamInviteAccept'));
const TermsOfService = lazyWithRetry(() => import('./src/pages/TermsOfService'));
const SmsTerms = lazyWithRetry(() => import('./src/pages/SmsTerms'));
const SupportPage = lazyWithRetry(() => import('./src/pages/SupportPage'));
const CommunityPage = lazyWithRetry(() => import('./src/pages/CommunityPage'));
import FeedbackPage from './src/pages/FeedbackPage';
const AdminExportPage = lazyWithRetry(() => import('./src/pages/admin/AdminExportPage'));
const AdminStatsPage = lazyWithRetry(() => import('./src/pages/admin/AdminStatsPage'));
// Presenter mode: even the server check lives in its own small chunk, fetched only on /admin/presenter.
const PresenterGate = lazyWithRetry(() => import('./src/presenter/PresenterGate'));
const AdminFeatureFlagsPage = lazyWithRetry(() => import('./src/pages/admin/AdminFeatureFlagsPage'));
const AdminInstitutesPage = lazyWithRetry(() => import('./src/pages/admin/AdminInstitutesPage'));
const AdminSignupsPage = lazyWithRetry(() => import('./src/pages/admin/AdminSignupsPage'));
const AdminFloridaLicenseFilePage = lazyWithRetry(() => import('./src/pages/admin/AdminFloridaLicenseFilePage'));
const EMTSettings = lazyWithRetry(() => import('./src/components/EMTSettings'));
const EmployerSettings = lazyWithRetry(() => import('./src/pages/employer/EmployerSettings'));
const EmployerCareersPage = lazyWithRetry(() => import('./src/pages/employer/EmployerCareersPage'));

// UI Components
import Skeleton from './src/components/ui/skeleton';

const PublicJobBoard = lazyWithRetry(() => import('./src/pages/public/PublicJobBoard'));

// Loading fallback for Suspense
const LoadingFallback = () => (
  <div className="p-6">
    <Skeleton className="h-10 w-64" />
    <Skeleton className="h-40 w-full mt-4" />
  </div>
);

/** Renders Community inside the correct app shell: EMTLayout for EMT, EmployerNav for employer. */
function CommunityShell() {
  const { user, role, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/" replace />;
  // Institutes have no community section: the EMT shell's every nav link bounced them back home.
  if (String(role) === 'institute') return <Navigate to="/institute/dashboard" replace />;
  if (role === 'employer') {
    return (
      <>
        <EmployerNav />
        <CommunityPage />
      </>
    );
  }
  return (
    <EMTLayout>
      <CommunityPage />
    </EMTLayout>
  );
}

export default function App() {
  // Check for Firebase config errors early (but don't block rendering)
  const configError = typeof window !== 'undefined' && (window as any).__FIREBASE_CONFIG_ERROR__;
  if (configError) {
    // Still render router so error screen can be shown
    // AppErrorBoundary will catch and show FirebaseConfigError
  }

  return (

    <Router>

      <AuthProvider>
        <ThemeProvider>
        <FeatureFlagsProvider>
        <UnreadCountsProvider>
          {/* Navigation watcher to debug redirects */}
          <DebugNavWatcher />
          {/* Every Back returns to where the user came from (tab, filters, scroll). */}
          <NavMemory />
          <OfflineBanner />
          {/* Only while signed in to a practice company (profiles.isPracticeAccount). */}
          <PracticeBanner />
          {/* The red number: unread count in the tab title and on the installed app icon. */}
          <UnreadBadgeSync />
          {/* Links into a #section scroll to it and outline it for a moment. */}
          <HashSectionHighlighter />
          {/* Presenter mode: a way back to /admin/presenter, only in the presenter's own window. */}
          <PresenterReturnButton />
          
          {/* Build version stamp (dev only) */}
          {import.meta.env.DEV && <BuildVersion />}

      {/* One boundary for every lazily loaded page (most pages are split out of the entry bundle). */}
      <Suspense fallback={<LoadingFallback />}>
      <Routes>

        {/* Root: unauth=MainLandingPage, auth=redirect to dashboard */}

        <Route path="/" element={<RootRoute />} />

        {/* Sign-in (LandingPage = role selection + sign-in form) */}
        <Route path="/signin" element={<LandingPage />} />
        <Route path="/login" element={<LandingPage />} />
        {/* Sign-up (dedicated create-account flow) */}
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/sso/role-selection" element={<SSORoleSelection />} />

        {/* Legal Pages (Public) */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/verify-manager/:requestId" element={<VerifyManagerPage />} />
        <Route path="/team-invite/:inviteId" element={<TeamInviteAccept />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/sms-terms" element={<SmsTerms />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/contact" element={<Navigate to="/support" replace />} />

        {/* Public Profile (Public - no auth required) */}
        <Route
          path="/p/:emtUid"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicProfile />
            </Suspense>
          }
        />

        {/* EMT Login (for existing users or direct navigation) */}

        <Route path="/emt-welcome" element={<EMTWelcome />} />

        {/* EMT Registration Flow - Protected from employer access */}

        <Route path="/register/step1" element={<EMTRouteGuard><EMTRegStep1 /></EMTRouteGuard>} />

        <Route path="/register/step2" element={<EMTRouteGuard><EMTRegStep2 /></EMTRouteGuard>} />

        <Route path="/register/step3" element={<EMTRouteGuard><EMTRegStep3 /></EMTRouteGuard>} />

        <Route path="/register/step4" element={<EMTRouteGuard><EMTRegStep4 /></EMTRouteGuard>} />

        {/* New sign-up order (flag emt_onboarding_v2, see src/lib/onboarding/emtSignupFlow.ts). With the
            flag off these send the visitor to step 3 / step 4, so an old link never strands anyone. */}
        <Route path="/register/quick-questions" element={<EMTRouteGuard><EMTRegQuickQuestions /></EMTRouteGuard>} />

        <Route path="/register/work-experience" element={<EMTRouteGuard><EMTRegWorkExperience /></EMTRouteGuard>} />

        {/* Medical/Training Institute account type */}
        <Route path="/institute-registration" element={<InstituteRegistration />} />
        <Route path="/institute/dashboard" element={<InstituteDashboard />} />
        <Route path="/institute/renewals" element={<InstituteRenewals />} />
        <Route path="/institute/programs" element={<InstitutePrograms />} />
        <Route path="/institute/locations" element={<InstituteLocations />} />
        <Route path="/institute/profile" element={<InstituteProfile />} />
        {/* Graduate tracking: classes, the roster of students sharing progress, one read-only graduate */}
        <Route path="/institute/classes" element={<InstituteClasses />} />
        <Route path="/institute/classes/:classId" element={<InstituteClassRoster />} />
        <Route path="/institute/classes/:classId/graduates/:memberId" element={<InstituteGraduate />} />
        {/* The class signup link an institute gives its students (signed out, or a signed-in EMT) */}
        <Route path="/class/:classId" element={<ClassInvitePage />} />
        <Route path="/institute/settings" element={<InstituteSettings />} />
        {/* EMT consent: one-click "stop institute messages" (signed-in EMT, or an emailed signed link) */}
        <Route path="/unsubscribe/institutes" element={<InstituteContactUnsubscribe />} />
        {/* Arevina's one-click institute review (signed email link, no sign-in; or a super admin) */}
        <Route path="/review/institute" element={<InstituteReviewPage />} />
        <Route path="/institutes/:instituteId" element={<InstitutePublicPage />} />

        {/* EMT Authenticated Screens — single layout so nav is consistent (Dashboard, Performance, Account, Jobs, Community, Past Logs, etc.) */}
        {/* PublicJobGate: on /jobs/:jobId, anyone who is not a signed-in candidate gets the public posting
            (the link employers share); every other route here is unchanged. */}
        <Route element={<PublicJobGate><ProtectedRoute requireRole="emt"><EMTLayout /></ProtectedRoute></PublicJobGate>}>
          <Route
            path="dashboard"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <EMTDashboard />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="career-wallet"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <CareerWallet />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route path="wallet" element={<Navigate to="/career-wallet" replace />} />
          {/* Documents employers asked for (Request documents, flag document_requests): upload and link. */}
          <Route
            path="documents/requests"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <DocumentRequestsPage />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          {/* Payroll details an employer that hired this person asked for (flag hr_sensitive_handoff). */}
          <Route
            path="payroll-details"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <PayrollDetailsPage />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          {/* New hire checklists employers gave this person (flag hire_checklist on the employer's side). */}
          <Route
            path="hire-checklist"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <HireChecklistPage />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="performance"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <EMTPerformance />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route path="readiness" element={<Navigate to="/performance" replace />} />
          <Route
            path="account"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <EMTAccountDetails />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="profile"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <EMTAccountDetails />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="settings"
            element={
              <AppErrorBoundary>
                <EMTSettings />
              </AppErrorBoundary>
            }
          />
          <Route path="notifications" element={<EMTNotificationsChat />} />
          <Route
            path="messages"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Messages />
              </Suspense>
            }
          />
          <Route
            path="jobs"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Jobs />
              </Suspense>
            }
          />
          <Route
            path="jobs/companies"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <EMTCompanyMap />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="jobs/:jobId/apply"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <JobApplication />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="jobs/:jobId"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <JobDetail />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="applications"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Applications />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="applications/booking/:bookingId"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <CandidateBookingPage />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="applications/interview-request/:requestId"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <InterviewRequestPage />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="applications/schedule/:inviteId"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <ScheduleInvitePage />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          {/* Find training: every reviewed training institute, filterable by credential and distance */}
          <Route
            path="find-training"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <FindTraining />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          {/* /training was a "coming soon" placeholder; it now lands on the real page */}
          <Route path="training" element={<Navigate to="/find-training" replace />} />
          <Route
            path="logs"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PastLogs />
              </Suspense>
            }
          />
          <Route
            path="credentialing"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <MyCredentialing />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          {/* Orientation invitations: accept, see what is missing, book a session (2026-10-08). */}
          <Route
            path="orientation"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <OrientationPage />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="orientation/:orgId"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <OrientationPage />
                </Suspense>
              </AppErrorBoundary>
            }
          />
          <Route
            path="feedback"
            element={
              <AppErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <FeedbackPage emtInLayout />
                </Suspense>
              </AppErrorBoundary>
            }
          />
        </Route>

        {/* Employer Public Routes */}

        <Route path="/employer-login" element={<EmployerLogin />} />

        <Route
          path="/employer/choose-plan"
          element={
            plansEnabled() ? <EmployerChoosePlan /> : <Navigate to="/employer/dashboard" replace />
          }
        />
        <Route path="/plans" element={<PlansRouteRedirect />} />
        <Route path="/pricing" element={<PlansRouteRedirect />} />

        {/* Public registration: never wrap with EmployerRouteGuard — that guard used to
            auto-create employer profiles on mount for authed profileless users. */}
        <Route path="/employer-registration" element={<EmployerRegistration />} />

        {/* Finish company setup (an employer account without a company yet). Was /employer/onboarding,
            which read like the retired onboarding checklist; the old address still works (2026-10-28). */}
        <Route
          path="/employer/company-setup"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerCompanySetup />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route path="/employer/onboarding" element={<Navigate to="/employer/company-setup" replace />} />

        {/* Mandatory backup-password step for brand-new Google-signup employers, before they
            reach the dashboard — same requirement as EMT Google signup (see /register/step1). */}
        <Route
          path="/employer/backup-password"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerSsoBackupPassword />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Employer Protected Routes */}

        <Route
          path="/employer/dashboard"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerDashboardHome />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/jobs/new"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerPostJob />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/jobs/emergency"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerPostEmergencyJob />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/jobs"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerJobsPage />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* The employer's own view of one job: the posting as candidates see it, with its actions (2026-10-29). */}
        <Route
          path="/employer/jobs/:jobId"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerJobView />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/jobs/:jobId/edit"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerPostJob />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/jobs/:jobId/edit-emergency"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerPostEmergencyJob />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/jobs/:jobId/applicants"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerJobApplicants />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/messages"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerMessages />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/notifications"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerNotifications />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route path="/employer/profile/:emtId" element={<EmployerProfileRedirect />} />

        <Route
          path="/employer/search"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerSearch />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/*
          Paused, not dead: the purchases-based "Saved Profiles" feature is scaffolded for a
          future paid-access build-out (see organizations/{orgId}/purchases, which is
          deliberately kept). Nothing writes to that collection yet, so EmployerPurchasedProfiles
          would only ever show empty/misleading data to real employers. Redirect to the dashboard
          until the paid feature is actually built.
        */}
        <Route path="/employer/purchased" element={<Navigate to="/employer/dashboard" replace />} />

        <Route
          path="/employer/account"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerAccount />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Careers page settings: the company's own page at arevina.com/jobs/<page name> (2026-11-05). */}
        <Route
          path="/employer/careers-page"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerCareersPage />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/settings"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerSettings />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/feedback"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <FeedbackPage />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/company-profile/edit"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerEditCompany />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/company-profile"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerCompanyProfile />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/company-checklist"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerCompanyChecklist />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/checklist"
          element={<Navigate to="/employer/company-checklist" replace />}
        />

        <Route
          path="/employer/matches"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerMatches />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route path="/employer/edit-company" element={<Navigate to="/employer/company-profile/edit" replace />} />

        {/* Public per-employer job board — the page an employer links to from their own careers site */}
        <Route path="/careers/:orgId" element={
          <Suspense fallback={<LoadingFallback />}>
            <PublicJobBoard />
          </Suspense>
        } />

        <Route path="/org/:orgId" element={
          <Suspense fallback={<LoadingFallback />}>
            <OrganizationPublicProfile />
          </Suspense>
        } />

        {/* Old Applications address: keeps its query (?appId= from older notifications opens that application). */}
        <Route path="/employer/applications" element={<RedirectKeepingQuery to="/employer/workforce" set={{ tab: 'applications' }} />} />

        {/* Onboarding: training session & requirements, sessions calendar, onboarding list (email
            blast) and new hire template — one surface (2026-09-24). The old email-list URL redirects. */}
        <Route
          path="/employer/team-onboarding"
          element={
            <ProtectedRoute requireRole="employer">
              <RequireEmployer>
                <EmployerTeamOnboarding />
              </RequireEmployer>
            </ProtectedRoute>
          }
        />
        <Route path="/employer/onboarding-blast" element={<Navigate to="/employer/team-onboarding" replace />} />

        {/* Credentials: expiring across team and applicants, and the new-hire export (2026-10-08). */}
        <Route
          path="/employer/credentials"
          element={
            <ProtectedRoute requireRole="employer">
              <RequireEmployer>
                <Suspense fallback={<LoadingFallback />}>
                  <EmployerCredentials />
                </Suspense>
              </RequireEmployer>
            </ProtectedRoute>
          }
        />
        {/* "Results" became "Insights" (2026-10-14): the old address keeps working. */}
        <Route path="/employer/results" element={<Navigate to="/employer/insights" replace />} />

        <Route
          path="/employer/workforce"
          element={
            <RetiredTabRedirect tab="onboarding" to="/employer/team-onboarding">
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerWorkforce />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
            </RetiredTabRedirect>
          }
        />

        <Route
          path="/employer/scheduling"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerScheduling />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/insights"
          element={
            <ProtectedRoute requireRole="employer">
              <RequireEmployer>
                <Suspense fallback={<LoadingFallback />}>
                  <EmployerHiringInsights />
                </Suspense>
              </RequireEmployer>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employer/job-benchmarks"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerJobBenchmarks />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/applications/:jobId/:emtId"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <RequireEmployer>
                  <EmployerApplicationDetail />
                </RequireEmployer>
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/emt-profile/:emtId"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerEmtProfile />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Plans and profile purchases are off (no billing); like /employer/purchased, go to the dashboard. */}
        <Route path="/purchases" element={<Navigate to="/employer/dashboard" replace />} />

        {/* Legacy routes for backward compatibility */}
        <Route
          path="/employer-dashboard"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerDashboard />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer-search"
          element={
            <ProtectedRoute requireRole="employer">
              <Suspense fallback={<LoadingFallback />}>
                <EmployerSearch />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Legacy alias of /employer/purchased -- same paused feature, see note above. */}
        <Route path="/employer-purchased-profiles" element={<Navigate to="/employer/dashboard" replace />} />

        <Route path="/company-profile" element={<Navigate to="/employer/company-profile" replace />} />

        {/* Community feed (EMT + Employer; uses normal app shell per role) */}
        <Route
          path="/community"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <CommunityShell />
            </Suspense>
          }
        />

        {/* Dev Navigation Hub (for testing all screens) */}
        {import.meta.env.DEV && (
          <>
            <Route path="/dev/navigation" element={<NavigationHub />} />
            <Route path="/dev/employer-navigation" element={<EmployerNavigationHub />} />
            <Route path="/dev/check" element={<DevHealthCheck />} />
          </>
        )}

        {/* Debug Firebase (dev only) */}
        {import.meta.env.DEV && (
          <Route path="/debug/firebase" element={<DebugFirebase />} />
        )}

        {/* Build Version Debug (dev only) */}
        {import.meta.env.DEV && (
          <Route path="/debug/version" element={<BuildVersionPage />} />
        )}

        {/* Platform admin tools */}
        <Route path="/admin/export" element={<AdminExportPage />} />
        <Route path="/admin/stats" element={<AdminStatsPage />} />
        <Route path="/admin/feature-flags" element={<AdminFeatureFlagsPage />} />
        <Route path="/admin/institutes" element={<AdminInstitutesPage />} />
        <Route path="/admin/signups" element={<AdminSignupsPage />} />
        <Route path="/admin/florida-license-file" element={<AdminFloridaLicenseFilePage />} />
        {/* Presenter mode: the server decides (allowlist, super admin, App Check, flag); everyone else goes to "/" */}
        <Route path="/admin/presenter" element={<PresenterGate />} />

        {/* Redirect unknown routes to login */}

        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
      </Suspense>
        </UnreadCountsProvider>
        </FeatureFlagsProvider>
        </ThemeProvider>
      </AuthProvider>

    </Router>

  );

}
